import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { emailOffer } from "@/lib/api";
import { toast } from "sonner";

export default function EmailDialog({ open, onOpenChange, offerId, defaultEmail }) {
  const [recipient, setRecipient] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await emailOffer(offerId, recipient || null);
      toast.success(`Sent to ${recipient || "your email"}`);
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.error(
          err?.response?.data?.detail ||
            "Email is not configured. Add RESEND_API_KEY to enable."
        );
      } else {
        toast.error(err?.response?.data?.detail || "Send failed");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0F1F38] border-white/10 text-[#F5F1E8] max-w-md"
        data-testid="email-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <EnvelopeSimple weight="bold" className="h-5 w-5 text-[#C9A961]" />
            Email this offer
          </DialogTitle>
          <DialogDescription className="text-[#9AA5B5]">
            We&apos;ll send a beautifully formatted HTML copy of the offer
            (plus the public link).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-2">
          <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
            Send to
          </Label>
          <Input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={defaultEmail || "you@example.com"}
            className="bg-[#0A1628] border-white/15 focus-visible:border-[#C9A961] focus-visible:ring-0 text-[#F5F1E8] rounded-sm"
            data-testid="email-recipient-input"
          />
          <p className="text-xs text-[#808A9B]">
            Leave blank to send to your own login email.
          </p>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            className="text-[#9AA5B5] hover:text-white hover:bg-white/5"
            data-testid="email-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending}
            className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
            data-testid="email-send-btn"
          >
            {sending ? (
              <>
                <div className="mr-2 h-4 w-4 rounded-full border-2 border-black border-t-transparent animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <PaperPlaneTilt weight="fill" className="mr-2 h-4 w-4" />
                Send email
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
